import { noop } from 'lodash';
import React, { memo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';

import { Actions } from '../../components/Actions/Actions';
import { FormField } from '../../components/FormField/FormField';
import { Layout } from '../../components/Layout/Layout';
import { Popover } from '../../components/Popover/Popover';
import { Text } from '../../components/Text/Text';
import { TextArea } from '../../components/TextArea/TextArea';
import { useToggle } from '../../hooks/useToggle';
import { Nullable } from '../../types/Nullable';

// detail object data provided by consumer
type Details = object;

// submit detail object including `Feedback` internal state
export type SubmitDetails = Details & {
  category: Nullable<string>;
  comment: string;
  value: Nullable<boolean>;
};

interface Props {
  /** Indicates the feedback value:
   * - true -> like
   * - false -> dislike
   * - null -> unspecified
   */
  value: Nullable<boolean>;
  /** Variant of the feedback button component */
  variant?: 'default' | 'subtle';
  /** Captures the updated feedback value */
  onChange?: (updatedValue: Nullable<boolean>) => void;
  /** Placeholder text for the comment textarea */
  placeholders?: {
    like: string;
    dislike: string;
  };
  /** Submit additional feedback details */
  submitDetails?: {
    /** Required callback to submit the button */
    onSubmit: (submitDetails: SubmitDetails) => void;
    details?: Details;
    /** Disables the submit button */
    disabled?: boolean;
    /** Indicates the submit button is loading */
    isLoading?: boolean;
    /** Minimum length of the comment */
    minLength?: number;
    /** Render additional details */
    render?: (details: Details) => React.ReactNode;
    /** Returns category labels based on the like/dislike value */
    categories?: (value: boolean) => string[];
    /** Callback when cancelling the submission */
    onCancel?: () => void;
  };
}

export const Feedback = memo(
  ({
    value: initialValue,
    variant = 'default',
    placeholders,
    submitDetails,
    onChange = noop,
  }: Props) => {
    const intl = useIntl();
    const formatNumber = useFormatNumber();
    const [category, setCategory] = useState<Nullable<string>>(null);
    const [comment, setComment] = useState('');
    const [value, setValue] = useState<Nullable<boolean>>(initialValue);
    const [isDetailsVisible, _toggleDetails, showDetails, hideDetails] =
      useToggle();

    const handleUpdateValue = (updatedValue: Nullable<boolean>) => () => {
      setValue(updatedValue);
      onChange(updatedValue);
      setCategory(null);
      if (submitDetails && updatedValue !== null) {
        showDetails();
      } else {
        hideDetails();
      }
    };

    const handleCancel = () => {
      setCategory(null);
      setComment('');
      hideDetails();
      submitDetails?.onCancel?.();
    };
    const actionLevel = variant === 'subtle' ? 'subtle' : undefined;
    const feedbackActions = (
      <Actions
        actions={[
          {
            icon: 'reaction-thumbsup',
            mode: 'icon',
            status: value === true ? 'active' : undefined,
            text: intl.formatMessage({
              id: 'Feedback.likeButton',
              defaultMessage: 'Like',
              description: 'Like feedback button label',
            }),
            tooltip: intl.formatMessage({
              id: 'Feedback.likeTooltip',
              defaultMessage: 'Like',
              description: 'Like feedback button tooltip',
            }),
            level: actionLevel,
            onClick: handleUpdateValue(value === true ? null : true),
          },
          {
            icon: 'reaction-thumbsdown',
            mode: 'icon',
            status: value === false ? 'active' : undefined,
            text: intl.formatMessage({
              id: 'Feedback.dislikeButton',
              defaultMessage: 'Dislike',
              description: 'Dislike feedback button label',
            }),
            tooltip: intl.formatMessage({
              id: 'Feedback.dislikeTooltip',
              defaultMessage: 'Dislike',
              description: 'Dislike feedback button tooltip',
            }),
            level: actionLevel,
            onClick: handleUpdateValue(value === false ? null : false),
          },
        ]}
      />
    );

    if (submitDetails) {
      const {
        categories,
        details = {},
        disabled,
        isLoading,
        render,
        onSubmit,
        minLength,
      } = submitDetails;

      const categoryLabels =
        value !== null && categories ? categories(value) : [];
      const categoryActions = categoryLabels.map((label) => ({
        text: label,
        status: category === label ? ('active' as const) : undefined,
        onClick: () => setCategory(category === label ? null : label),
      }));

      const isBelowMinLength = !!minLength && comment.length < minLength;
      return (
        <Popover
          isVisible={isDetailsVisible}
          maxWidth="none"
          trigger={feedbackActions}
          placement="top-start"
          onClickOutside={handleCancel}
        >
          <Layout direction="column" spacing={2} w="form.width.m">
            <Text variant="body-medium">
              {intl.formatMessage({
                id: 'Feedback.thankYouForYourFeedback',
                defaultMessage: 'Thank you for providing feedback!',
                description: 'Thank you for your feedback message',
              })}
            </Text>
            {categoryActions.length > 0 && (
              <Actions mode="chip" size="s" actions={categoryActions} wrap />
            )}
            <FormField<string, false>
              optional
              footer={`${(comment ?? '').length}/${formatNumber(
                MAX_COMMENT_LENGTH,
                {
                  unit: intl.formatMessage({
                    id: 'Feedback.charactersUnit',
                    defaultMessage: 'characters',
                    description: 'Unit for character count in feedback comment',
                  }),
                },
              )}`}
              input={TextArea}
              inputProps={{
                maxLength: MAX_COMMENT_LENGTH,
                maxH: 150,
                rows: 3,
                width: 'form.width.m',
              }}
              label={
                value
                  ? intl.formatMessage({
                      id: 'Feedback.whatWorkedWellLabel',
                      defaultMessage: 'What worked well?',
                      description:
                        'Label for feedback comment textarea when user liked the message',
                    })
                  : intl.formatMessage({
                      id: 'Feedback.whatCouldImproveLabel',
                      defaultMessage: 'What could improve?',
                      description:
                        'Label for feedback comment textarea when user disliked the message',
                    })
              }
              name="feedback comments"
              placeholder={
                value
                  ? (placeholders?.like ??
                    intl.formatMessage({
                      id: 'Feedback.whatWorkedWellPlaceholder',
                      defaultMessage: 'What made this helpful?',
                      description:
                        'Default placeholder for feedback comment textarea when user liked',
                    }))
                  : (placeholders?.dislike ??
                    intl.formatMessage({
                      id: 'Feedback.whatCouldImprovePlaceholder',
                      defaultMessage: 'What would have made this better?',
                      description:
                        'Default placeholder for feedback comment textarea when user disliked',
                    }))
              }
              value={comment}
              onChange={(updatedComment) => setComment(updatedComment ?? '')}
            />
            {render?.(details)}
            <Layout alignSelf="flex-end">
              <Actions
                actions={[
                  {
                    text: intl.formatMessage({
                      id: 'Feedback.closeButton',
                      defaultMessage: 'Close',
                      description: 'Close feedback button label',
                    }),
                    level: 'tertiary',
                    onClick: handleCancel,
                  },
                  {
                    disabled: disabled || isBelowMinLength,
                    isLoading,
                    level: 'primary',
                    tooltip: isBelowMinLength
                      ? intl.formatMessage(
                          {
                            id: 'Feedback.minLengthTooltip',
                            defaultMessage: 'Minimum {minLength} characters',
                            description:
                              'Tooltip for minimum length of the comment',
                          },
                          {
                            minLength,
                          },
                        )
                      : undefined,
                    text: intl.formatMessage({
                      id: 'Feedback.submitButton',
                      defaultMessage: 'Submit Feedback',
                      description: 'Submit feedback button label',
                    }),
                    onClick: () => {
                      onSubmit({ ...details, category, comment, value });
                      handleCancel();
                    },
                  },
                ]}
              />
            </Layout>
          </Layout>
        </Popover>
      );
    } else {
      return feedbackActions;
    }
  },
);

const MAX_COMMENT_LENGTH = 1000;
